import React, { useState } from 'react';
import {
  DataGridPremium,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-premium';
import { DataGridPro } from '@mui/x-data-grid-pro';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { string } from 'prop-types';
import PersistentDataGrid from 'common/PersistentDataGrid';
import NoResultsBanner from 'common/template-parts/NoResultsBanner';

// Mock data generators
const generateMockRows = (count = 50) => {
  const statuses = ['Active', 'Pending', 'Completed', 'Cancelled', 'In Transit'];
  const products = ['Crude Oil', 'Gasoline', 'Diesel', 'Jet Fuel', 'Natural Gas'];
  const locations = ['Houston TX', 'Cushing OK', 'New York NY', 'Los Angeles CA', 'Chicago IL'];
  const owners = ['Company A', 'Company B', 'Company C', 'Company D', 'Company E'];

  return Array.from({ length: count }, (_, index) => ({
    batchId: `BATCH-${String(index + 1).padStart(5, '0')}`,
    createdAt: new Date(2026, 0, Math.floor(Math.random() * 90) + 1),
    date: new Date(2026, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1),
    id: index + 1,
    location: locations[Math.floor(Math.random() * locations.length)],
    owner: owners[Math.floor(Math.random() * owners.length)],
    price: parseFloat((Math.random() * 100 + 50).toFixed(2)),
    product: products[Math.floor(Math.random() * products.length)],
    status: statuses[Math.floor(Math.random() * statuses.length)],
    volume: Math.floor(Math.random() * 10000) + 1000,
  }));
};

const mockData = generateMockRows(50);

// Status chip renderer
const StatusChipRenderer = ({ value }) => {
  const getStatusColor = status => {
    const colors = {
      Active: 'success',
      Cancelled: 'error',
      Completed: 'info',
      'In Transit': 'primary',
      Pending: 'warning',
    };
    return colors[status] || 'default';
  };

  return <Chip color={getStatusColor(value)} label={value} size="small" sx={{ fontWeight: 500 }} />;
};

StatusChipRenderer.propTypes = {
  value: string.isRequired,
};

// Custom toolbar for DataGrid
const CustomToolbar = () => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton variant="dataTable" />
    <GridToolbarFilterButton variant="dataTable" />
    <GridToolbarDensitySelector variant="dataTable" />
    <GridToolbarExport variant="dataTable" />
    <Box sx={{ flexGrow: 1 }} />
    <GridToolbarQuickFilter size="small" variant="outlined" />
  </GridToolbarContainer>
);

// Base column definitions
const getBaseColumns = () => [
  {
    field: 'batchId',
    headerName: 'Batch ID',
    pinnable: true,
    width: 150,
  },
  {
    field: 'status',
    headerName: 'Status',
    renderCell: params => <StatusChipRenderer value={params.value} />,
    width: 140,
  },
  {
    cellClassName: 'css-heap-redact',
    field: 'owner',
    headerName: 'Owner',
    width: 150,
  },
  {
    field: 'product',
    headerName: 'Product',
    width: 150,
  },
  {
    field: 'location',
    headerName: 'Location',
    width: 150,
  },
  {
    field: 'volume',
    headerName: 'Volume (bbls)',
    type: 'number',
    valueFormatter: params => params.value?.toLocaleString(),
    width: 150,
  },
  {
    field: 'price',
    headerName: 'Price ($)',
    type: 'number',
    valueFormatter: params => `$${params.value?.toFixed(2)}`,
    width: 120,
  },
  {
    field: 'date',
    headerName: 'Available Date',
    type: 'date',
    valueFormatter: params => format(params.value, 'MM/dd/yyyy'),
    width: 140,
  },
  {
    field: 'createdAt',
    headerName: 'Created At',
    type: 'dateTime',
    valueFormatter: params => format(params.value, 'MM/dd/yyyy HH:mm'),
    width: 160,
  },
];

// Aggregation columns for Premium features
const getAggregationColumns = () => [
  {
    field: 'batchId',
    headerName: 'Batch ID',
    width: 150,
  },
  {
    field: 'status',
    headerName: 'Status',
    renderCell: params => <StatusChipRenderer value={params.value} />,
    width: 140,
  },
  {
    field: 'owner',
    headerName: 'Owner',
    width: 150,
  },
  {
    field: 'product',
    headerName: 'Product',
    width: 150,
  },
  {
    aggregable: true,
    field: 'volume',
    headerName: 'Volume (bbls)',
    type: 'number',
    valueFormatter: params => params.value?.toLocaleString(),
    width: 150,
  },
  {
    aggregable: true,
    field: 'price',
    headerName: 'Price ($)',
    type: 'number',
    valueFormatter: params =>
      typeof params.value === 'number' ? `$${params.value?.toFixed(2)}` : params.value,
    width: 120,
  },
];

const MuiDataGridPreview = () => {
  const [selectionModel, setSelectionModel] = useState([]);

  return (
    <Container maxWidth="xl" sx={{ py: 8 }}>
      <Typography gutterBottom variant="h3">
        MUI DataGrid Component Preview
      </Typography>
      <Typography
        color="text.secondary"
        sx={{
          marginBottom: '16px',
        }}
        variant="body1"
      >
        This page showcases various implementations of MUI DataGrids used throughout the
        application. Use this for design validation, layout testing, and data display verification.
      </Typography>
      <Divider sx={{ my: 3 }} />
      <Stack spacing={4}>
        {/* 1. Basic DataGridPremium */}
        <Card>
          <CardContent>
            <Typography gutterBottom variant="h5">
              1. DataGridPremium - Basic Implementation
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                marginBottom: '16px',
              }}
              variant="body2"
            >
              Standard DataGridPremium with toolbar, filtering, sorting, and column management.
            </Typography>
            <Box sx={{ height: 500, width: '100%' }}>
              <DataGridPremium
                checkboxSelection
                columns={getBaseColumns()}
                disableRowSelectionOnClick
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                }}
                rows={mockData}
                slots={{ toolbar: CustomToolbar }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* 2. DataGridPremium with Row Selection */}
        <Card>
          <CardContent>
            <Typography gutterBottom variant="h5">
              2. DataGridPremium - With Row Selection
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                marginBottom: '16px',
              }}
              variant="body2"
            >
              Shows row selection functionality. Selected rows: {selectionModel.length}
            </Typography>
            <Box sx={{ height: 400, width: '100%' }}>
              <DataGridPremium
                checkboxSelection
                columns={getBaseColumns()}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                }}
                onRowSelectionModelChange={newSelection => setSelectionModel(newSelection)}
                pageSizeOptions={[10, 25, 50]}
                rows={mockData.slice(0, 15)}
                rowSelectionModel={selectionModel}
                slots={{ toolbar: CustomToolbar }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* 3. DataGridPremium with Pinned Columns */}
        <Card>
          <CardContent>
            <Typography gutterBottom variant="h5">
              3. DataGridPremium - Pinned Columns
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                marginBottom: '16px',
              }}
              variant="body2"
            >
              Batch ID is pinned to the left. Scroll horizontally to see the effect.
            </Typography>
            <Box sx={{ height: 400, width: '100%' }}>
              <DataGridPremium
                columns={getBaseColumns()}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                  pinnedColumns: { left: ['batchId'] },
                }}
                pageSizeOptions={[10, 25, 50]}
                rows={mockData.slice(0, 15)}
                slots={{ toolbar: CustomToolbar }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* 4. DataGridPremium with Grouping */}
        <Card>
          <CardContent>
            <Typography gutterBottom variant="h5">
              4. DataGridPremium - Row Grouping
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                marginBottom: '16px',
              }}
              variant="body2"
            >
              Grouped by Status and Product. Expand rows to see details.
            </Typography>
            <Box sx={{ height: 500, width: '100%' }}>
              <DataGridPremium
                columns={getAggregationColumns()}
                initialState={{
                  aggregation: {
                    model: {
                      price: 'avg',
                      volume: 'sum',
                    },
                  },
                  pagination: { paginationModel: { pageSize: 25 } },
                  rowGrouping: {
                    model: ['status', 'product'],
                  },
                }}
                pageSizeOptions={[10, 25, 50]}
                rows={mockData}
                slots={{ toolbar: CustomToolbar }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* 5. PersistentDataGrid Implementation */}
        <Card>
          <CardContent>
            <Typography gutterBottom variant="h5">
              5. PersistentDataGrid - With State Persistence - No Custom Toolbar (Default used)
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                marginBottom: '16px',
              }}
              variant="body2"
            >
              Custom wrapper around DataGridPremium that saves column order, filters, and sort state
              to localStorage. Try reordering columns, then refresh the page.
            </Typography>
            <Box sx={{ height: 500, width: '100%' }}>
              <PersistentDataGrid
                checkboxSelection
                columns={getBaseColumns()}
                disableRowSelectionOnClick
                id="mui-datagrid-preview-persistent"
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                }}
                pageSizeOptions={[10, 25, 50]}
                rows={mockData}
              />
            </Box>
          </CardContent>
        </Card>

        {/* 6. DataGridPro Implementation */}
        <Card>
          <CardContent>
            <Typography gutterBottom variant="h5">
              6. DataGridPro - Basic Implementation
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                marginBottom: '16px',
              }}
              variant="body2"
            >
              DataGridPro variant (mid-tier) without Premium features like aggregation and grouping.
            </Typography>
            <Box sx={{ height: 400, width: '100%' }}>
              <DataGridPro
                checkboxSelection
                columns={getBaseColumns()}
                disableRowSelectionOnClick
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                }}
                pageSizeOptions={[10, 25, 50]}
                rows={mockData.slice(0, 20)}
                slots={{ toolbar: CustomToolbar }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* 7. Compact Density */}
        <Card>
          <CardContent>
            <Typography gutterBottom variant="h5">
              7. DataGridPremium - Compact Density
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                marginBottom: '16px',
              }}
              variant="body2"
            >
              Shows more rows in less space with compact density mode.
            </Typography>
            <Box sx={{ height: 400, width: '100%' }}>
              <DataGridPremium
                columns={getBaseColumns()}
                density="compact"
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                pageSizeOptions={[10, 25, 50]}
                rows={mockData.slice(0, 25)}
                slots={{ toolbar: CustomToolbar }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* 8. Custom Cell Renderers */}
        <Card>
          <CardContent>
            <Typography gutterBottom variant="h5">
              8. DataGridPremium - Custom Cell Renderers
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                marginBottom: '16px',
              }}
              variant="body2"
            >
              Demonstrates custom cell renderers with chips, tooltips, and formatted values.
            </Typography>
            <Box sx={{ height: 400, width: '100%' }}>
              <DataGridPremium
                columns={[
                  {
                    field: 'batchId',
                    headerName: 'Batch ID',
                    renderCell: params => (
                      <Typography
                        sx={{
                          '&:hover': { color: 'primary.dark' },
                          color: 'primary.main',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                      >
                        {params.value}
                      </Typography>
                    ),
                    width: 150,
                  },
                  {
                    field: 'status',
                    headerName: 'Status',
                    renderCell: params => (
                      <Tooltip placement="top" title={`Status: ${params.value}`}>
                        <div>
                          <StatusChipRenderer value={params.value} />
                        </div>
                      </Tooltip>
                    ),
                    width: 140,
                  },
                  {
                    field: 'volume',
                    headerName: 'Volume',
                    renderCell: params => (
                      <Box
                        sx={{
                          alignItems: 'center',
                          display: 'flex',
                          gap: 1,
                        }}
                      >
                        <Typography fontWeight={600} variant="body2">
                          {params.value?.toLocaleString()}
                        </Typography>
                        <Typography color="text.secondary" variant="caption">
                          bbls
                        </Typography>
                      </Box>
                    ),
                    width: 150,
                  },
                  {
                    field: 'price',
                    headerName: 'Price',
                    renderCell: params => (
                      <Chip
                        color={params.value > 75 ? 'success' : 'default'}
                        label={`$${params.value?.toFixed(2)}`}
                        size="small"
                        variant="outlined"
                      />
                    ),
                    width: 140,
                  },
                  ...getBaseColumns().slice(4),
                ]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                }}
                pageSizeOptions={[10, 25, 50]}
                rows={mockData.slice(0, 15)}
                slots={{ toolbar: CustomToolbar }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* 9. Loading Overlay */}
        <Card>
          <CardContent>
            <Typography gutterBottom variant="h5">
              9. PersistentDataGrid - Loading Overlay
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                marginBottom: '16px',
              }}
              variant="body2"
            >
              Demonstrates the default loading overlay state (DataLoadingCard). The grid displays a
              loading indicator while data is being fetched.
            </Typography>
            <Box sx={{ height: 400, width: '100%' }}>
              <PersistentDataGrid
                columns={getBaseColumns()}
                id="mui-datagrid-preview-loading"
                loading
                rows={[]}
              />
            </Box>
          </CardContent>
        </Card>

        {/* 10. No Rows Overlay */}
        <Card>
          <CardContent>
            <Typography gutterBottom variant="h5">
              10. PersistentDataGrid - No Rows Overlay
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                marginBottom: '16px',
              }}
              variant="body2"
            >
              Shows the default empty state overlay (NoAvailableCard) when no data is available.
              Provides a consistent empty state experience across the application.
            </Typography>
            <Box sx={{ height: 400, width: '100%' }}>
              <PersistentDataGrid
                columns={getBaseColumns()}
                id="mui-datagrid-preview-no-rows"
                rows={[]}
              />
            </Box>
          </CardContent>
        </Card>

        {/* 11. PersistentDataGrid - No Results Banner */}
        <Card>
          <CardContent>
            <Typography gutterBottom variant="h5">
              11. PersistentDataGrid - No Results Banner
            </Typography>
            <Typography color="text.secondary" paragraph variant="body2">
              Demonstrates a no-results state using the reusable NoResultsBanner as the empty
              overlay.
            </Typography>
            <Box sx={{ height: 400, width: '100%' }}>
              <PersistentDataGrid
                columns={getBaseColumns()}
                id="mui-datagrid-preview-no-results-banner"
                rows={[]}
                slots={{
                  noRowsOverlay: () => (
                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        height: '100%',
                        justifyContent: 'center',
                        width: '100%',
                      }}
                    >
                      <NoResultsBanner
                        primaryText="Select a product and location to see Authorization Sequences"
                        secondaryText="Start by applying at least 1 product and 1 location filter to see results."
                      />
                    </Box>
                  ),
                }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* 12. PersistentDataGrid - Exporting Data */}
        <Card>
          <CardContent>
            <Typography gutterBottom variant="h5">
              12. PersistentDataGrid - Exporting Data
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                marginBottom: '16px',
              }}
              variant="body2"
            >
              Demonstrates the loading overlay state (DataProcessingCard) when exporting data. The
              grid displays a processing indicator while data is being exported.
            </Typography>
            <Box sx={{ height: 400, width: '100%' }}>
              <PersistentDataGrid
                columns={getBaseColumns()}
                id="mui-datagrid-preview-exporting"
                isExporting
                isProcessing
                loading
                rows={[]}
              />
            </Box>
          </CardContent>
        </Card>

        {/* 13. PersistentDataGrid - Processing Data */}
        <Card>
          <CardContent>
            <Typography gutterBottom variant="h5">
              13. PersistentDataGrid - Processing Data
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                marginBottom: '16px',
              }}
              variant="body2"
            >
              Demonstrates the loading overlay state (DataProcessingCard) when processing data. The
              grid displays a processing indicator while data is being processed.
            </Typography>
            <Box sx={{ height: 400, width: '100%' }}>
              <PersistentDataGrid
                columns={getBaseColumns()}
                id="mui-datagrid-preview-processing"
                isProcessing
                loading
                rows={[]}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Summary Card */}
        <Card sx={{ bgcolor: 'primary.50' }}>
          <CardContent>
            <Typography gutterBottom variant="h6">
              Implementation Notes
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2">
                • <strong>DataGridPremium:</strong> Full-featured grid with grouping, aggregation,
                and advanced filtering
              </Typography>
              <Typography variant="body2">
                • <strong>DataGridPro:</strong> Mid-tier grid with basic features, tree data, and
                column pinning
              </Typography>
              <Typography variant="body2">
                • <strong>PersistentDataGrid:</strong> Custom wrapper that persists user preferences
                (column order, filters, sort)
              </Typography>
              <Typography variant="body2">
                • All grids support: Sorting, Filtering, Column Management, Density Control, Export
              </Typography>
              <Typography variant="body2">
                • Custom cell renderers can be used for chips, links, tooltips, and formatted values
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
};

export default MuiDataGridPreview;
